import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Scale } from "lucide-react";

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#030712] relative">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/5 via-transparent to-teal-900/5" />

      <nav className="relative z-10 border-b border-white/5 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Button 
            data-testid="back-btn"
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </nav>

      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <Scale className="w-10 h-10 text-[#00E0FF]" />
            <h1 className="text-4xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
              TERMS OF SERVICE
            </h1>
          </div>
          <p className="text-gray-400">Last Updated: January 2026</p>
        </motion.div>

        <Card className="glass-effect">
          <CardContent className="p-8 space-y-6 text-gray-300">
            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>1. ACCEPTANCE OF TERMS</h2>
              <p className="leading-relaxed">
                By accessing and using TradeInsights ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>2. SERVICE DESCRIPTION</h2>
              <p className="leading-relaxed mb-3">
                TradeInsights provides AI-powered market analysis, predictions, and educational content for stocks and cryptocurrencies. The Service includes:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Real-time market data and analytics</li>
                <li>AI-generated trading predictions and recommendations</li>
                <li>Portfolio tracking and management tools</li>
                <li>Price alert notifications</li>
                <li>Educational resources and learning materials</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>3. USER ACCOUNTS</h2>
              <p className="leading-relaxed mb-3">
                To access certain features, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate and complete information</li>
                <li>Maintain the security of your account credentials</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Be responsible for all activities under your account</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>4. PAYMENT TERMS</h2>
              <p className="leading-relaxed mb-3">
                The Service offers both subscription and pay-per-use pricing models:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Monthly Subscription:</strong> $4.99/month for unlimited predictions</li>
                <li><strong>Pay-Per-Prediction:</strong> $0.99 per AI prediction credit</li>
                <li>Payments are processed securely through Stripe</li>
                <li>Subscriptions automatically renew unless cancelled</li>
                <li>Credits are non-refundable once purchased</li>
                <li>Subscription cancellations take effect at the end of the billing period</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>5. ACCEPTABLE USE</h2>
              <p className="leading-relaxed mb-3">
                You agree NOT to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Use the Service for any illegal purposes</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Share your account credentials with others</li>
                <li>Scrape or automate access to the Service without permission</li>
                <li>Redistribute or resell Service content</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>6. INTELLECTUAL PROPERTY</h2>
              <p className="leading-relaxed">
                All content, features, and functionality of the Service are owned by TradeInsights and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our express written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>7. TERMINATION</h2>
              <p className="leading-relaxed">
                We reserve the right to suspend or terminate your account at any time for violations of these Terms or for any other reason. Upon termination, your right to use the Service will immediately cease. Paid subscriptions will not be refunded upon termination for cause.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>8. LIMITATION OF LIABILITY</h2>
              <p className="leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, TRADEINSIGHTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF THE SERVICE.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>9. CHANGES TO TERMS</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of material changes via email or through the Service. Continued use of the Service after changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Unbounded' }}>10. CONTACT</h2>
              <p className="leading-relaxed">
                For questions about these Terms, please contact us at: <a href="mailto:legal@tradeinsights.com" className="text-[#00E0FF] hover:underline">legal@tradeinsights.com</a>
              </p>
            </section>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
